import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams, useSearchParams } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { CompareProvider } from "./contexts/CompareContext";
import { AuthCallback, ProtectedRoute } from "./components/AuthCallback";
import { Toaster } from "./components/ui/sonner";
import TerapeutAI from "./components/TerapeutAI";
import { CompareBar } from "./components/CompareBar";

// Redirect component that preserves query params
const RedirectWithParams = ({ to }) => {
  const [searchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const destination = queryString ? `${to}?${queryString}` : to;
  return <Navigate to={destination} replace />;
};

// City URL Redirect Component
const CityRedirect = () => {
  const { oldCitySlug } = useParams();
  // Don't redirect if already has the prefix
  if (oldCitySlug.startsWith('centro-rehabilitacion-')) {
    return null;
  }
  return <Navigate to={`/ciudad/centro-rehabilitacion-${oldCitySlug}`} replace />;
};

// Pages
import LandingPage from "./pages/LandingPage";
import { CentersDirectory, CenterDetail } from "./pages/CentersDirectory";
import { ProfessionalsDirectory, ProfessionalDetail } from "./pages/ProfessionalsDirectory";
import { BlogPage, ArticleDetail } from "./pages/BlogPage";
import AuthorPage from "./pages/AuthorPage";
import MarketingCentrosPage from "./pages/MarketingCentrosPage";
import DenunciasLandingPage from "./pages/DenunciasLandingPage";
import { LoginPage, SelectRolePage } from "./pages/LoginPage";
import { CenterRegister, CenterDashboard } from "./pages/CenterDashboard";
import { ProfessionalDashboard } from "./pages/ProfessionalDashboard";
import { AdminDashboard } from "./pages/AdminDashboard";
import { UserProfilePage } from "./pages/UserProfilePage";
import { TreatmentCalculator } from "./pages/TreatmentCalculator";
import { TestsLanding, TestPage } from "./pages/TestsPage";
import { FormacionPage } from "./pages/FormacionPage";
import { AsesoriaPage } from "./pages/AsesoriaPage";
import AsesoriaConfirmacionPage from "./pages/AsesoriaConfirmacionPage";
import { AppLandingPage } from "./pages/AppLandingPage";
import { AgregarCentroPage } from "./pages/AgregarCentroPage";
import { UnirseComoProfesionalPage } from "./pages/UnirseComoProfesionalPage";
import { PaymentConfirmation, PaymentCancelled, PaymentSuccess, PaymentFailed, PaymentPending } from "./pages/PaymentPages";
import { KlapPaymentSuccess, KlapPaymentFailure } from "./pages/KlapPaymentPages";
import { WorkshopsDirectory, WorkshopDetail } from "./pages/WorkshopsDirectory";
import { WorkshopDashboard, WorkshopRegister } from "./pages/WorkshopDashboard";
import GuiaElegirCentroPage from "./pages/GuiaElegirCentroPage";
import WorkshopLandingPage from "./pages/WorkshopLandingPage";
import FamiliarLandingPage from "./pages/FamiliarLandingPage";
import OpinionCentrosPage from "./pages/OpinionCentrosPage";
import CasosDeExitoPage from "./pages/CasosDeExitoPage";
import EncuestaPage from "./pages/EncuestaPage";
import TestimoniosPage from "./pages/TestimoniosPage";
import TratamientoAdiccionesOnlinePage from "./pages/TratamientoOnlinePage";
import CoachAdiccionesPage from "./pages/CoachAdiccionesPage";
import { ProfessionalsBySpecialty, ProfessionalsBySpecialtyCity } from "./pages/ProfessionalsSEO";
import { CentersByModality, CentersByModalityLocation } from "./pages/CentersSEO";
import { WorkshopsByType, WorkshopsByTypeLocation } from "./pages/WorkshopsSEO";
import { CitiesDirectoryPage, CityDetailPage } from "./pages/CitiesPage";
import { SalesDashboard } from "./pages/SalesDashboard";
import { AAGroupsPage } from "./pages/AAGroupsPage";
import ComparePage from "./pages/ComparePage";
import CentrosRehabilitacionDrogasGratisLanding from "./pages/CentrosRehabilitacionDrogasGratisLanding";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ContactPage from "./pages/ContactPage";
import GraciasPage from "./pages/GraciasPage";
import QuienesSomosPage from "./pages/QuienesSomosPage";
import { PlanesPage } from "./pages/PlanesPage";
import ZonaCeroPage from "./pages/ZonaCeroPage";
import SendaPuenteAltoPage from "./pages/SendaPuenteAltoPage";
import RegionPage, { RegionsHubPage } from "./pages/RegionPage";
import AudiencePage from "./pages/AudiencePage";
import PastaBasePage from "./pages/content/adicciones/PastaBasePage";
import PatologiaDualPage from "./pages/content/adicciones/PatologiaDualPage";
import QuoteRequestFloating from "./components/QuoteRequestFloating";
import CotizacionLanding from "./pages/CotizacionLanding";
import ExperienciaNelson from "./pages/ExperienciaNelson";
import ExperienciaYoFuturo from "./pages/ExperienciaYoFuturo";
import ExperienciaEspejo from "./pages/ExperienciaEspejo";
import ExperienciasLanding from "./pages/ExperienciasLanding";
import Reto21DiasPage from "./pages/Reto21DiasPage";
import Reto21LandingPage from "./pages/Reto21LandingPage";
import EstudioReto21Page from "./pages/EstudioReto21Page";
import TestimonioReto21Page from "./pages/TestimonioReto21Page";
import TerapeutIAPage from "./pages/TerapeutIAPage";
import Reto21InsightsAdmin from "./pages/admin/Reto21InsightsAdmin";
import Reto21TestimoniosAdmin from "./pages/admin/Reto21TestimoniosAdmin";
import NotFoundPage from "./pages/NotFoundPage";

// Educational Content Pages
import {
  TratamientoCognitivoConductualPage,
  TratamientoAmbulatorioPage,
  TratamientoOnlinePage,
  GuiaTratamientosPage,
  CentrosRehabilitacionGratisPage,
  TratamientoAdiccionesLandingPage,
  AdiccionesHubPage,
  AlcoholPage,
  CocainaPage,
  MarihuanaPage,
  MedicamentosPage,
  LudopatiaPage,
  ConductualesPage,
  // New Treatment Landing Pages
  TratamientosHubPage,
  AmbulatorioPage,
  ResidencialPage,
  OnlinePage,
  DesintoxicacionPage,
  CognitivoPage,
  ConductualPage,
  CognitivoConductualPage,
  HolisticoPage,
  IntegrativoPage
} from "./pages/content";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Router wrapper to handle session_id in hash
const AppRouter = () => {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id - must be synchronous, not in useEffect
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/centros-de-rehabilitacion-drogas-chile" element={<CentersDirectory />} />
      {/* Redirect old URL to new URL preserving query params */}
      <Route path="/centros" element={<RedirectWithParams to="/centros-de-rehabilitacion-drogas-chile" />} />
      <Route path="/centros/:centerId" element={<CenterDetail />} />
      <Route path="/comparar" element={<ComparePage />} />
      
      {/* SEO-friendly center routes */}
      <Route path="/centro/:modality" element={<CentersByModality />} />
      <Route path="/centro/:modality/:location" element={<CentersByModalityLocation />} />
      
      <Route path="/profesionales" element={<ProfessionalsDirectory />} />
      <Route path="/profesionales/:professionalId" element={<ProfessionalDetail />} />
      
      {/* SEO-friendly professional routes (like Doctoralia) */}
      <Route path="/profesional/:specialty" element={<ProfessionalsBySpecialty />} />
      <Route path="/profesional/:specialty/:city" element={<ProfessionalsBySpecialtyCity />} />
      
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<ArticleDetail />} />

      {/* Author profile pages (SEO E-E-A-T) */}
      <Route path="/autor/:slug" element={<AuthorPage />} />
      <Route path="/author/:slug" element={<AuthorPage />} />

      {/* B2B Marketing landing for centers */}
      <Route path="/marketing-centros-rehabilitacion" element={<MarketingCentrosPage />} />
      <Route path="/marketing-para-centros" element={<MarketingCentrosPage />} />

      {/* Denuncias y reclamos contra centros (canal periodístico confidencial) */}
      <Route path="/denuncias-centros-rehabilitacion-chile" element={<DenunciasLandingPage />} />

      {/* Workshops */}
      <Route path="/talleres" element={<WorkshopsDirectory />} />
      <Route path="/talleres/:workshopId" element={<WorkshopDetail />} />
      
      {/* SEO-friendly workshop routes */}
      <Route path="/taller/:workshopType" element={<WorkshopsByType />} />
      <Route path="/taller/:workshopType/:location" element={<WorkshopsByTypeLocation />} />

      {/* Calculator */}
      <Route path="/calculadora" element={<TreatmentCalculator />} />

      {/* Tools/Tests */}
      <Route path="/herramientas" element={<TestsLanding />} />
      <Route path="/herramientas/:testId" element={<TestPage />} />

      {/* Encuesta post-cotización + Testimonios */}
      <Route path="/encuesta/:token" element={<EncuestaPage />} />
      <Route path="/testimonios" element={<TestimoniosPage />} />

      {/* Tratamiento Online (landing propia con menú y footer independientes) */}
      <Route path="/tratamiento-adicciones-online" element={<TratamientoAdiccionesOnlinePage />} />

      {/* Coaching Nelson González — storytelling parallax */}
      <Route path="/coach-adicciones" element={<CoachAdiccionesPage />} />
      
      {/* SEO Guide Pages */}
      <Route path="/como-elegir-centro-rehabilitacion" element={<GuiaElegirCentroPage />} />

      {/* Formación */}
      <Route path="/formacion" element={<FormacionPage />} />

      {/* Asesoría */}
      <Route path="/asesoria" element={<AsesoriaPage />} />
      <Route path="/asesoria/confirmacion" element={<AsesoriaConfirmacionPage />} />

      {/* App Landing */}
      <Route path="/app" element={<AppLandingPage />} />

      {/* Cities SEO */}
      <Route path="/ciudades" element={<CitiesDirectoryPage />} />
      <Route path="/ciudad/:cityPath" element={<CityDetailPage />} />

      {/* Landing Pages Publicitarias */}
      <Route path="/agregar-centro" element={<AgregarCentroPage />} />
      <Route path="/unirse-profesional" element={<UnirseComoProfesionalPage />} />
      <Route path="/registrar-taller" element={<WorkshopLandingPage />} />
      <Route path="/como-ayudar-a-un-familiar-con-adiccion" element={<FamiliarLandingPage />} />
      <Route path="/ser-querido-necesita-ayuda" element={<Navigate to="/como-ayudar-a-un-familiar-con-adiccion" replace />} />
      <Route path="/opinion-centros" element={<OpinionCentrosPage />} />
      <Route path="/casos-de-exito" element={<CasosDeExitoPage />} />
      {/* Alias 301-equivalente: clientas que llegan desde links viejos o sugerencias del chat IA */}
      <Route path="/agregar-taller" element={<Navigate to="/registrar-taller" replace />} />
      
      {/* Grupos de Apoyo */}
      <Route path="/grupos-aa" element={<AAGroupsPage />} />

      {/* SEO Landing - Centros Gratis */}
      <Route path="/centros-de-rehabilitacion-de-drogas-gratis" element={<CentrosRehabilitacionDrogasGratisLanding />} />

      {/* Legal Pages */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/contacto" element={<ContactPage />} />
      <Route path="/gracias" element={<GraciasPage />} />
      <Route path="/quienes-somos" element={<QuienesSomosPage />} />
      <Route path="/planes" element={<PlanesPage />} />
      <Route path="/zona-cero" element={<ZonaCeroPage />} />
      <Route path="/senda-puente-alto" element={<SendaPuenteAltoPage />} />

      {/* Educational Content Pages */}
      {/* Redirect old /educacion to new /tratamientos */}
      <Route path="/educacion" element={<Navigate to="/tratamientos" replace />} />
      <Route path="/tratamiento-adicciones" element={<Navigate to="/tratamientos" replace />} />
      <Route path="/tratamiento-cognitivo-conductual" element={<TratamientoCognitivoConductualPage />} />
      <Route path="/tratamiento-ambulatorio" element={<TratamientoAmbulatorioPage />} />
      <Route path="/tratamiento-online" element={<TratamientoOnlinePage />} />
      <Route path="/guia-tratamientos" element={<GuiaTratamientosPage />} />
      {/* /centros-rehabilitacion-gratis → 301-equivalente a la URL canónica (evita auto-canibalización SEO) */}
      <Route path="/centros-rehabilitacion-gratis" element={<Navigate to="/centros-de-rehabilitacion-de-drogas-gratis" replace />} />
      
      {/* Addictions Hub and Individual Pages */}
      <Route path="/adicciones" element={<AdiccionesHubPage />} />
      <Route path="/adicciones/alcohol" element={<AlcoholPage />} />
      <Route path="/adicciones/cocaina" element={<CocainaPage />} />
      <Route path="/adicciones/marihuana" element={<MarihuanaPage />} />
      <Route path="/adicciones/medicamentos" element={<MedicamentosPage />} />
      <Route path="/adicciones/ludopatia" element={<LudopatiaPage />} />
      <Route path="/adicciones/conductuales" element={<ConductualesPage />} />
      <Route path="/adicciones/pasta-base" element={<PastaBasePage />} />
      <Route path="/adicciones/patologia-dual" element={<PatologiaDualPage />} />

      {/* Regional SEO Pages */}
      <Route path="/regiones" element={<RegionsHubPage />} />
      <Route path="/region/:slug" element={<RegionPage />} />

      {/* Audience-targeted SEO Pages */}
      <Route path="/publico/:audience" element={<AudiencePage />} />

      {/* Quote/Cotización Landing */}
      <Route path="/cotizar" element={<CotizacionLanding />} />
      <Route path="/experiencia" element={<ExperienciaNelson />} />
      <Route path="/yo-del-futuro" element={<ExperienciaYoFuturo />} />
      <Route path="/espejo" element={<ExperienciaEspejo />} />
      <Route path="/experiencias" element={<ExperienciasLanding />} />
      <Route path="/mi-reto-21-dias" element={<Reto21DiasPage />} />
      <Route path="/reto-21-dias" element={<Reto21LandingPage />} />
      <Route path="/estudio-reto-21-dias" element={<EstudioReto21Page />} />
      <Route path="/testimonio-reto-21" element={<TestimonioReto21Page />} />
      <Route path="/terapeutia" element={<TerapeutIAPage />} />
      <Route path="/terapeut-ia" element={<Navigate to="/terapeutia" replace />} />
      <Route path="/terapeut-ai" element={<Navigate to="/terapeutia" replace />} />

      {/* NEW: Treatments Hub and Landing Pages */}
      <Route path="/tratamientos" element={<TratamientosHubPage />} />
      <Route path="/tratamiento/ambulatorio" element={<AmbulatorioPage />} />
      <Route path="/tratamiento/residencial" element={<ResidencialPage />} />
      <Route path="/tratamiento/residencial/cognitivo" element={<CognitivoPage />} />
      <Route path="/tratamiento/residencial/conductual" element={<ConductualPage />} />
      <Route path="/tratamiento/residencial/cognitivo-conductual" element={<CognitivoConductualPage />} />
      <Route path="/tratamiento/residencial/holistico" element={<HolisticoPage />} />
      <Route path="/tratamiento/residencial/integrativo" element={<IntegrativoPage />} />
      <Route path="/tratamiento/online" element={<OnlinePage />} />
      <Route path="/tratamiento/desintoxicacion" element={<DesintoxicacionPage />} />
      {/* Redirects for existing pages and special cases */}
      <Route path="/tratamiento/grupos-apoyo" element={<Navigate to="/grupos-aa" replace />} />
      <Route path="/tratamiento/gratis" element={<Navigate to="/centros-de-rehabilitacion-de-drogas-gratis" replace />} />

      {/* Payment Pages */}
      <Route path="/cita/confirmacion" element={<PaymentConfirmation />} />
      <Route path="/cita/cancelada" element={<PaymentCancelled />} />
      <Route path="/pago/exitoso" element={<PaymentSuccess />} />
      <Route path="/pago/fallido" element={<PaymentFailed />} />
      <Route path="/pago/pendiente" element={<PaymentPending />} />
      <Route path="/payment/success" element={<KlapPaymentSuccess />} />
      <Route path="/payment/failure" element={<KlapPaymentFailure />} />

      <Route path="/login" element={<LoginPage />} />

      {/* Auth Callback */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <SelectRolePage />
        </ProtectedRoute>
      } />

      {/* Role Selection */}
      <Route path="/select-role" element={
        <ProtectedRoute>
          <SelectRolePage />
        </ProtectedRoute>
      } />

      {/* Center Routes */}
      <Route path="/center/register" element={
        <ProtectedRoute>
          <CenterRegister />
        </ProtectedRoute>
      } />
      <Route path="/center/dashboard" element={
        <ProtectedRoute requiredRole="center">
          <CenterDashboard />
        </ProtectedRoute>
      } />

      {/* Professional Routes */}
      <Route path="/professional/dashboard" element={
        <ProtectedRoute requiredRole="professional">
          <ProfessionalDashboard />
        </ProtectedRoute>
      } />

      {/* Workshop Routes */}
      <Route path="/workshop/register" element={<WorkshopRegister />} />
      <Route path="/workshop/dashboard" element={
        <ProtectedRoute requiredRole="workshop">
          <WorkshopDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/taller" element={
        <ProtectedRoute requiredRole="workshop">
          <WorkshopDashboard />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/reto21-insights" element={
        <ProtectedRoute requiredRole="admin">
          <Reto21InsightsAdmin />
        </ProtectedRoute>
      } />
      <Route path="/admin/reto21-testimonios" element={
        <ProtectedRoute requiredRole="admin">
          <Reto21TestimoniosAdmin />
        </ProtectedRoute>
      } />

      {/* Sales Dashboard */}
      <Route path="/ventas" element={
        <ProtectedRoute requiredRole="sales">
          <SalesDashboard />
        </ProtectedRoute>
      } />

      {/* User Profile */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <UserProfilePage />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App font-body">
      <BrowserRouter>
        <AuthProvider>
          <CompareProvider>
            <ScrollToTop />
            <AppRouter />
            <CompareBar />
            <TerapeutAI />
            <QuoteRequestFloating />
            <Toaster position="top-right" richColors />
          </CompareProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
